<!-- =========================================================================================
  File Name: ECommerceShop.vue
  Description: eCommerce Shop Page
  ----------------------------------------------------------------------------------------
  Item Name: Vuexy - Vuejs, HTML & Laravel Admin Dashboard Template
    Author: Pixinvent
  Author URL: http://www.themeforest.net/user/pixinvent
========================================================================================== -->

<template>
  <div id="main-container">
    <div class="vx-row mt-5">
      <div class="vx-col sm:w-1/2 w-full mb-base">
        <h1>{{ helpers.product.getPreferredName(product) }} (ID: {{ product.product_id }})</h1>
      </div>
      <div class="vx-col sm:w-1/2 w-full mb-base">
        <vs-select
          v-bind:value="currentLocalization.language_id"
          v-on:input="refreshLocalization"
          class="ml-auto mr-4 vs-input-shadow-drop vs-select-no-border">
          <vs-select-item
            v-for="item in languages"
            :key="item.language_id" :value="item.language_id"
            :text="getLanguageLabel(item)"/>
        </vs-select>
      </div>
    </div>

    <div class="vx-row">
      <!--- First Column ---->
      <div class="vx-col md:w-1/2 w-full mb-base">
        <ProductGeneralEditor
          :product="product"
          :companies="companies"
          :all-product-types="allProductTypes"
          v-on:unsavedChangesUpdate="unsavedChanges.general = $event"
          v-on:productChanged="reFetchProduct"
        />
        <ProductPropertiesEditor
          class="mt-8"
          :product="product"
          :language="new types.Language(currentLocalization.language)"
          v-on:unsavedChangesUpdate="unsavedChanges.baseLocalization = $event"
          v-on:productChanged="reFetchProduct"
        />
        <ProductCyclopediaPropertiesEditor
          class="mt-8"
          :product="product"
          :language="new types.Language(currentLocalization.language)"
          :cyclopedias="cyclopedias"
          v-on:unsavedChangesUpdate="unsavedChanges.baseLocalization = $event"
          v-if="!product.enrollment_kit"
        />
        <FrequenciesListCard
          class="mt-8"
          :product="product"
          v-if="!product.enrollment_kit"
        />
        <AvailableInCountriesCard
          class="mt-8"
          :product="product"
        />
      </div>
      <!--- Second Column ---->
      <div class="vx-col md:w-1/2 w-full mb-base">
        <ProductLocalizationEditor
          :localization="currentLocalization"
           v-on:unsavedChangesUpdate="unsavedChanges.baseLocalization = $event"
           v-on:productChanged="reFetchProduct"
        />
        <CompanyProductData
          class="mt-8"
          v-on:onProductChange="fetchAll"
          :productId="productId"
          :companyProductData="product.company_product_data"
        />
        <RelatedProductsSelectionCard
          class="mt-8"
          :product="product"
        />
        <ProductCompanyGroupsEditor
          class="mt-8"
          :company="new types.Company(product.company)"
          :product="new types.Product(product)"
        />
        <ProductCyclopediaCategoriesEditor
          class="mt-8"
          :product="new types.Product(product)"
          :cyclopedias="cyclopedias"
          :language="new types.Language(currentLocalization.language)"
          v-if="!product.enrollment_kit"
        />
        <DnaTraitScoresCard class="mt-8" />
        <ProductFaceScanMetricsEditor
          class="mt-8"
          :product="product"
        />
      </div>
    </div>

    <div class="vx-row py-2 mb-2 w-full no-gutter">
      <div class="vx-col w-full">
        <vs-button class="ml-auto" color="danger" icon="delete_outline" type="gradient" @click="confirmDeleteProduct">Delete Product</vs-button>
      </div>
    </div>
  </div>
</template>

<script>
  import api from '@/http/api';
  import types from '@/models';
  import jsonHtml from '@/misc/jsonHtmlPrinter'
  import ProductLocalizationEditor from './components/ProductLocalizationEditorCard';
  import ProductGeneralEditor from './components/ProductGeneralEditorCard';
  import ProductPropertiesEditor from './components/ProductPropertiesEditorCard';
  import ProductFaceScanMetricsEditor from './components/ProductFaceScanMetricsEditorCard';
  import ProductCompanyGroupsEditor from './components/ProductCompanyGroupsEditorCard';
  import ProductCyclopediaPropertiesEditor
    from './components/ProductCyclopediaPropertiesCard';
  import ProductCyclopediaCategoriesEditor
    from './components/ProductCyclopediaCategoriesEditorCard';
  import FrequenciesListCard from './components/FrequenciesListCard';
  import AvailableInCountriesCard from './components/AvailableInCountriesCard';
  import RelatedProductsSelectionCard from './components/RelatedProductsSelectionCard';
  import DnaTraitScoresCard from './components/DnaTraitScoresCard';
  import CompanyProductData from './components/CompanyProductData';

  import helpers from '@/models/helpers';

  export default {
    components: {
      ProductLocalizationEditor,
      ProductGeneralEditor,
      ProductPropertiesEditor,
      ProductCyclopediaPropertiesEditor,
      ProductCompanyGroupsEditor,
      ProductFaceScanMetricsEditor,
      ProductCyclopediaCategoriesEditor,
      FrequenciesListCard,
      AvailableInCountriesCard,
      RelatedProductsSelectionCard,
      DnaTraitScoresCard,
      CompanyProductData
    },
    mounted() {
      this.fetchAll();
    },
    data() {
      return {
        jsonHtml: jsonHtml,
        unsavedChanges: {
          general: false,
          baseLocalization: false,
          companyGroups: false,
        },
        types: types,
        isLoading: false,
        product: new types.Product(),
        companies: [new types.Company()],
        cyclopedias: [new types.Cyclopedia()],
        currentLocalization: new types.Localization(),
        languages: [new types.Language()],
        allProductTypes: [new types.ProductType()],
        helpers: helpers,
      }
    },
    computed: {
      productId() {
        return this.$route.query.id || null;
      },
      defaultLanguageId() {
        return Number(this.$route.query.lang) || null;
      },
      howManyUnsavedChanges() {
        let count = 0;
        let keys = Object.keys(this.unsavedChanges);
        for (let i = 0; i < keys.length; i++) {
          if (this.unsavedChanges[keys[i]]) {
            count++;
          }
        }
        return count;
      },
    },
    watch: {
      isLoading(oldVal, newVal) {
        if (!newVal) {
          this.$vs.loading();
        } else {
          this.$vs.loading.close();
        }
      }
    },
    methods: {
      showNotification(success, message) {
        this.$vs.notify({
          title: success ? 'Success' : 'Error',
          text: message ? message : '',
          color: success ? 'success' : 'danger',
          icon: success ? 'check_circle_outline' : 'error'});
      },
      confirmDeleteProduct() {
        this.$vs.dialog({
          type: 'confirm',
          color: 'danger',
          acceptText: 'DELETE',
          title: `DELETE PRODUCT`,
          text: `Are you sure you want to delete this product? This action is permanent and may have unintended consequences. (Users who have scanned for this product will have it disappear from their reports, etc.)`,
          accept: () => {
            this.deleteProduct();
          }
        })
      },
      async deleteProduct() {
        this.$vs.loading();
        const success = await api.deleteProduct(this.product);
        this.$vs.loading.close();
        this.showNotification(success, !success ? 'COULD NOT DELETE PRODUCT' : '');
        if (success) {
          this.$router.push(`/content/products/list`).catch(()=> { });
        }
      },
      refreshLocalization(languageId, force = false) {
        if (!force && this.howManyUnsavedChanges > 0 && languageId !== this.currentLocalization.language_id) {
          this.$vs.dialog({
            type: 'confirm',
            color: 'danger',
            title: `Confirm`,
            text: `Are you sure you want to change languages? Your unsaved ${this.currentLocalization.language.name} changes will be lost.`,
            accept: () => {
              this.refreshLocalization(languageId, true)
            }
          });
          return;
        }

        let lang = this.languages.filter((l) => {
          return l.language_id === languageId
        })[0];

        this.isLoading = true;
        this.fetchProduct(lang).then(() => {
          /// getLocalization returns a copy/new instance
          this.currentLocalization = this.helpers.product.getLocalization(this.product, lang);
          this.isLoading = false;
        });
      },
      getLanguageLabel(language) {
        let label = `${language.name} (${language.code}`;
        if (language.country) label += `-${language.country}`;
        label += `)`;
        return label;
      },
      fetchAll() {
        this.isLoading = true;
        Promise.all([
          this.fetchLanguages(),
          this.fetchCyclopedias(),
          this.fetchProductTypes(),
          this.fetchCompanies(),
          //this.fetchProduct(),
        ]).then(() => {
          this.isLoading = false;
          this.refreshLocalization(this.defaultLanguageId || this.languages[0].language_id);
        });
      },
      async fetchCyclopedias() {
        this.cyclopedias = await api.fetchAllCyclopedias();
      },
      async fetchLanguages() {
        this.languages = await api.fetchAllLanguages();
      },
      async fetchProductTypes() {
        this.allProductTypes = await api.fetchProductTypes();
      },
      async fetchCompanies() {
        let companiesObj = await api.fetchAllCompanies();
        this.companies = companiesObj.companies;
      },
      async reFetchProduct() {
        // this api call takes so long it makes for a bad
        // experience. either we block the ui while it's loading,
        // which is a bad experience, or we let it run in the
        // background and update the ui when it's done which is a bad
        // experience IF the user has started to make other changes.
        // The odds they'll get out of sync with the server is pretty low
        // so re-loading the product every time a card updates is
        // probably unnecessary.

        // await this.fetchProduct();
      },
      async fetchProduct(language) {
        let product = await api.fetchLeanProduct(this.productId, language);
        if (product === null) {
          // todo: handle error
        }

        this.product = product;
      },
    },
    beforeRouteLeave(to, from, next) {
      if (this.howManyUnsavedChanges <= 0) {
        next();
      } else {
        // next(false);
        this.$vs.dialog({
          type: 'confirm',
          color: 'danger',
          title: `Confirm`,
          text: 'Are you sure you want to leave this page? You will lose your unsaved changes.',
          accept: () => {
            next()
          }
        });
      }
    },
  }

</script>


<style lang="scss">
  #main-container {

    .algolia-header {
      .algolia-filters-label {
        width: calc(260px + 2.4rem);
      }
    }

    #algolia-content-container {

      .vs-sidebar {
        position: relative;
        float: left;
      }
    }

    .algolia-search-input-right-aligned-icon {
      padding: 1rem 1.5rem;
    }

    .algolia-price-slider {
      min-width: unset;
    }

    .item-view-primary-action-btn {
      color: #2c2c2c !important;
      background-color: #f6f6f6;
      min-width: 50%;
    }

    .item-view-secondary-action-btn {
      min-width: 50%;
    }
  }

  .theme-dark {
    #main-container {
      #algolia-content-container {
        .vs-sidebar {
          background-color: #10163a;
        }
      }
    }
  }

  @media (min-width: 992px) {
    .vs-sidebar-rounded {
      .vs-sidebar {
        border-radius: .5rem;
      }

      .vs-sidebar--items {
        border-radius: .5rem;
      }
    }
  }

  @media (max-width: 992px) {
    #algolia-content-container {
      .vs-sidebar {
        position: absolute !important;
        float: none !important;
      }
    }
  }

</style>

